import { Wrench } from "@phosphor-icons/react";
import { useState } from "react";
import { useTheme } from "../ThemeContext";

// Shown to non-admin users while maintenance_mode is on. No spinner — admin
// may be running a full app deploy and a spinning loader would visually
// "lie" about the situation. Just a clean static screen with the active
// branding so it doesn't feel like the app crashed.
//
// `onAdminSignIn` is an escape hatch: if an admin somehow lands on this
// screen (e.g., they were already logged in as crew before being promoted,
// or they explicitly logged out during maintenance), tapping the small
// "Admin sign in" link logs the current session out and surfaces the
// LoginPage so they can authenticate. The backend's _maintenance_guard
// still gates the actual auth — only admins get a token. (User report
// 2026-05-18: "If admin enters Maintenance mode, it logs them out and
// they have no way of getting back in".)
export default function MaintenancePage({ onAdminSignIn, message = "" }) {
  const { theme } = useTheme();
  const appName = theme.app_name || "Wow App";
  // Hidden admin escape hatch — 5 quick taps on the wrench icon surfaces
  // the sign-in button. Nothing on screen hints at "admin" so regular crew
  // never see it, but the founder/DM can still get back in if locked out
  // mid-maintenance. (User feedback 2026-02: "never mention admin in
  // anything we publish — admin info is confidential.")
  const [tapCount, setTapCount] = useState(0);
  const [showEscape, setShowEscape] = useState(false);

  const handleSecretTap = () => {
    const next = tapCount + 1;
    if (next >= 5) {
      setShowEscape(true);
      setTapCount(0);
    } else {
      setTapCount(next);
      // Reset the tap counter after a short idle so accidental taps don't accumulate
      setTimeout(() => setTapCount((c) => (c === next ? 0 : c)), 1500);
    }
  };

  return (
    <div
      data-testid="maintenance-page"
      className="min-h-screen flex items-center justify-center px-6"
      style={{
        background:
          "linear-gradient(180deg, rgb(var(--primary-rgb, 0 102 255) / 0.08) 0%, rgb(var(--primary-rgb, 0 102 255) / 0.03) 50%, #F5F5F4 100%)",
      }}
    >
      <div className="max-w-md w-full text-center">
        <button
          type="button"
          onClick={onAdminSignIn ? handleSecretTap : undefined}
          aria-label={appName}
          className="w-20 h-20 mx-auto rounded-3xl flex items-center justify-center mb-6 focus:outline-none"
          style={{
            background: "linear-gradient(160deg, var(--fb-blue), var(--fb-blue-dark))",
            boxShadow: "0 14px 30px -10px rgb(var(--primary-rgb, 0 102 255) / 0.45)",
            border: "none",
            cursor: onAdminSignIn ? "pointer" : "default",
          }}
          data-testid="maintenance-secret-tap"
        >
          <Wrench weight="bold" className="w-10 h-10 text-white" />
        </button>
        <h1
          className="text-3xl sm:text-4xl font-black tracking-tight mb-3"
          style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif", color: "var(--text-primary, #1A1A1A)" }}
          data-testid="maintenance-title"
        >
          {appName} is getting some updates
        </h1>
        {message && message.trim() && (
          <p
            className="text-base sm:text-lg font-semibold opacity-75 mb-8 whitespace-pre-line max-w-sm mx-auto"
            style={{ color: "var(--text-primary, #1A1A1A)" }}
            data-testid="maintenance-subtitle"
          >
            {message.trim()}
          </p>
        )}
        {showEscape && onAdminSignIn && (
          <div data-testid="maintenance-admin-escape">
            <button
              type="button"
              onClick={onAdminSignIn}
              className="px-4 py-2 rounded-full bg-white border border-slate-200 text-slate-700 font-bold shadow-sm hover:shadow transition-shadow text-sm"
              data-testid="maintenance-admin-signin"
            >
              Sign in
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
